import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchLastSpeedtest } from '../api/dashboard';
import { useSpeedtestSocket } from '../hooks/useSpeedtestSocket';

const formatMetric = (value, fractionDigits = 1) => {
  if (value === null || value === undefined) return '--';
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return Number(numeric.toFixed(fractionDigits)).toLocaleString('ru-KZ');
  }
  return value;
};

const parseSpeed = (speedStr) => {
  if (!speedStr) return null;
  const match = speedStr.match(/([\d.]+)/);
  return match ? parseFloat(match[1]) : null;
};

const SpeedtestModalWebSocket = ({
  isOpen,
  onClose,
  deviceName,
  deviceIP,
}) => {
  const { t } = useTranslation()
  const { isConnected, testStatus, startTest, cancelTest, resetTest } = useSpeedtestSocket();
  const [lastData, setLastData] = useState(null);
  const [isLoadingLastData, setIsLoadingLastData] = useState(false);
  const [lastDataError, setLastDataError] = useState(null);
  const [showLastData, setShowLastData] = useState(false);

  // Сброс при закрытии модального окна
  useEffect(() => {
    if (!isOpen) {
      resetTest();
      setLastData(null);
      setShowLastData(false);
      setLastDataError(null);
    }
  }, [isOpen, resetTest]);

  const handleStart = () => {
    if (deviceIP && isConnected) {
      setShowLastData(false); // Скрываем последние данные при запуске нового теста
      setLastData(null);
      startTest(deviceIP);
    }
  };

  const handleCancel = () => {
    cancelTest();
  };

  const handleClose = () => {
    if (testStatus?.status === 'complete' || testStatus?.status === 'error' || testStatus?.status === 'cancelled' || !testStatus) {
      resetTest();
      onClose();
    }
  };

  const handleLoadLastData = async () => {
    if (!deviceIP) return;
    
    setIsLoadingLastData(true);
    setLastDataError(null);
    setShowLastData(false);
    
    try {
      const data = await fetchLastSpeedtest(deviceIP);
      if (data) {
        setLastData(data);
        setShowLastData(true);
      } else {
        setLastDataError(t('modals.speedtest.noSavedData'));
      }
    } catch (error) {
      setLastDataError(error.message || t('modals.speedtest.failedToLoadLastData'));
    } finally {
      setIsLoadingLastData(false);
    }
  };

  if (!isOpen) return null;

  const isRunning = testStatus && [' initializing', 'connecting', 'connected', 'starting', 'testing', 'testing_download', 'testing_upload', 'processing'].includes(testStatus.status);
  const isComplete = testStatus?.status === 'complete';
  const isError = testStatus?.status === 'error';
  const isCancelled = testStatus?.status === 'cancelled';

  // Используем последние данные, если они загружены и не запущен тест
  const displayData = showLastData && lastData && !isRunning ? lastData : (testStatus?.data || {});

  // Парсим финальные данные
  const downloadMbps = showLastData && lastData ? lastData.download : parseSpeed(displayData.download);
  const uploadMbps = showLastData && lastData ? lastData.upload : parseSpeed(displayData.upload);
  const packetLoss = showLastData && lastData ? lastData.loss : parseFloat(displayData.loss);

  // Текущие live значения (только если запущен тест)
  const liveDownload = isRunning ? testStatus?.currentDownload : null;
  const liveUpload = isRunning ? testStatus?.currentUpload : null;

  const metrics = [
    {
      label: t('modals.speedtest.download'),
      value: isRunning && liveDownload ? formatMetric(liveDownload) : (downloadMbps != null ? formatMetric(downloadMbps) : '--'),
      unit: (isRunning && liveDownload) || (downloadMbps != null) ? t('modals.speedtest.mbps') : '',
      accent: 'text-accent-cyan',
      isLive: isRunning && liveDownload,
    },
    {
      label: t('modals.speedtest.upload'),
      value: isRunning && liveUpload ? formatMetric(liveUpload) : (uploadMbps != null ? formatMetric(uploadMbps) : '--'),
      unit: (isRunning && liveUpload) || (uploadMbps != null) ? t('modals.speedtest.mbps') : '',
      accent: 'text-accent-pink',
      isLive: isRunning && liveUpload,
    },
    {
      label: t('modals.speedtest.loss'),
      value: isRunning && liveDownload ? formatMetric(packetLoss) : (downloadMbps != null ? formatMetric(packetLoss) : '--'),
      unit: packetLoss != null ? t('modals.speedtest.percent') : '',
      accent: 'text-warning',
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/70 px-4 py-10 backdrop-blur-2xl"
      role="dialog"
      aria-modal="true"
      aria-label={t('modals.speedtest.ariaLabel', { deviceName })}
    >
      <div className="pointer-events-none absolute inset-0 bg-brand-radial opacity-40" />

      <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-3xl border border-surface-border/40 bg-surface-raised/90 shadow-elevated-lg">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-surface-base/80 text-muted-200 transition hover:bg-brand-400/20 hover:text-white"
          aria-label={t('modals.speedtest.closeAriaLabel')}
        >
          <span className="material-icons text-lg">close</span>
        </button>

        <div className="max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col gap-6 p-6 md:p-8">
            <header className="flex flex-col gap-3 text-center">
              <span className="mx-auto inline-flex items-center gap-2 rounded-full bg-brand-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-100">
                {t('modals.speedtest.badge')}
              </span>
              <h3 className="font-display text-2xl text-white md:text-3xl">
                {deviceName || t('modals.speedtest.unknownDevice')}
              </h3>
              <p className="text-sm text-muted-400">
                {t('modals.speedtest.subtitle')}
              </p>
              
              {/* WebSocket статус */}
              {!isConnected && (
                <div className="flex items-center justify-center gap-2 text-xs text-warning">
                  <span className="material-icons text-sm">warning</span>
                  <span>{t('modals.speedtest.websocketDisconnected')}</span>
                </div>
              )}
            </header>

            {/* Прогресс бар */}
            {isRunning && testStatus?.progress !== undefined && (
              <div className="relative overflow-hidden rounded-full bg-surface-base/60 h-3">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-400 to-accent-cyan transition-all duration-300 ease-out rounded-full"
                  style={{ width: `${testStatus.progress}%` }}
                >
                  <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                </div>
              </div>
            )}

            {/* Сообщение статуса */}
            {testStatus?.message && (
              <div className="text-center">
                <p className="text-sm font-medium text-muted-300">
                  {testStatus.message}
                </p>
                {testStatus.progress !== undefined && (
                  <p className="mt-1 text-xs text-muted-500">
                    {testStatus.progress}%
                  </p>
                )}
              </div>
            )}

            {/* Ошибки */}
            {isError && (
              <div className="rounded-3xl border border-critical/40 bg-critical/10 p-4 text-sm text-critical">
                {testStatus.message || t('modals.speedtest.testError')}
              </div>
            )}

            {/* Отменен */}
            {isCancelled && (
              <div className="rounded-3xl border border-warning/40 bg-warning/10 p-4 text-sm text-warning">
                {t('modals.speedtest.testCancelled')}
              </div>
            )}

            {/* Сообщение о последних данных */}
            {showLastData && lastData && (
              <div className="rounded-3xl border border-brand-400/40 bg-brand-400/10 p-4 text-sm text-brand-100">
                <div className="flex items-center gap-2">
                  <span className="material-icons text-base">history</span>
                  <span>{t('modals.speedtest.lastSavedData')}</span>
                  {lastData.timestamp && (
                    <span className="ml-auto text-xs text-muted-400">
                      {new Date(lastData.timestamp).toLocaleString('ru-KZ', {
                        timeZone: 'Asia/Almaty',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Ошибка при загрузке последних данных */}
            {lastDataError && (
              <div className="rounded-3xl border border-warning/40 bg-warning/10 p-4 text-sm text-warning">
                {lastDataError}
              </div>
            )}

            {/* Метрики */}
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {metrics.map((metric) => (
                <article
                  key={metric.label}
                  className={`relative overflow-hidden rounded-3xl bg-surface-base/70 p-4 shadow-soft transition-all ${
                    metric.isLive ? 'ring-2 ring-brand-400/30' : showLastData && lastData ? 'ring-2 ring-brand-400/20' : ''
                  }`}
                >
                  <div className="absolute inset-0 opacity-0 transition duration-300 hover:opacity-100 bg-brand-radial" />
                  <div className="relative flex flex-col gap-2 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-500">
                        {metric.label}
                      </span>
                      {metric.isLive && (
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75"></span>
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-400"></span>
                        </span>
                      )}
                      {showLastData && lastData && !metric.isLive && (
                        <span className="material-icons text-xs text-muted-400">history</span>
                      )}
                    </div>
                    <span className={`text-2xl font-semibold text-white ${metric.accent} ${metric.isLive ? 'animate-pulse' : ''}`}>
                      {metric.value}
                      {metric.unit && (
                        <span className="ml-2 text-xs text-muted-400">{metric.unit}</span>
                      )}
                    </span>
                  </div>
                </article>
              ))}
            </section>

            {/* Кнопки управления */}
            <footer className="flex flex-col gap-4 rounded-3xl bg-surface-base/60 p-6 text-center shadow-soft">
              {isRunning && (
                <div className="flex flex-col items-center gap-4 text-sm text-muted-300">
                  <span className="relative flex h-12 w-12 items-center justify-center">
                    <span className="absolute inset-0 rounded-full border border-surface-border/50" />
                    <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-brand-300/80 animate-spin-sweep" />
                    <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-400/20 text-brand-100">
                      <span className="material-icons text-base">network_check</span>
                    </span>
                  </span>
                  <p>{t('modals.speedtest.testRunning')}</p>
                  
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-critical/40 bg-critical/10 px-4 py-3 text-sm font-semibold text-critical transition hover:bg-critical/20"
                  >
                    <span className="material-icons text-base">cancel</span>
                    {t('modals.speedtest.cancelTest')}
                  </button>
                </div>
              )}

              {!isRunning && (
                <div className="flex flex-col items-center gap-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-500">
                    {isComplete ? t('modals.speedtest.testComplete') : isError ? t('modals.speedtest.error') : isCancelled ? t('modals.speedtest.cancelled') : showLastData && lastData ? t('modals.speedtest.lastData') : t('modals.speedtest.startTest')}
                  </div>
                  
                  <div className="flex flex-col gap-2 w-full">
                    <button
                      type="button"
                      onClick={handleLoadLastData}
                      disabled={isLoadingLastData || !deviceIP}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-brand-400/40 bg-brand-400/10 px-4 py-3 text-sm font-semibold text-brand-100 transition hover:bg-brand-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingLastData ? (
                        <>
                          <span className="material-icons text-base animate-spin">hourglass_empty</span>
                          {t('modals.speedtest.loading')}
                        </>
                      ) : (
                        <>
                          <span className="material-icons text-base">history</span>
                          {t('modals.speedtest.loadLastData')}
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleStart}
                      disabled={!isConnected}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-linear px-4 py-3 text-sm font-semibold text-white shadow-brand transition hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="material-icons text-base">play_arrow</span>
                      {isComplete ? t('modals.speedtest.repeatTest') : t('modals.speedtest.startTest')}
                    </button>
                  </div>
                </div>
              )}
              
              {isComplete && (
                <p className="text-[11px] uppercase tracking-[0.25em] text-muted-500">
                  {t('modals.speedtest.testCompletedAt')} {new Date().toLocaleTimeString('ru-KZ')}
                </p>
              )}
              
              {showLastData && lastData && lastData.timestamp && (
                <p className="text-[11px] uppercase tracking-[0.25em] text-muted-500">
                  {t('modals.speedtest.dataSavedAt')} {new Date(lastData.timestamp).toLocaleString('ru-KZ', {
                    timeZone: 'Asia/Almaty',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeedtestModalWebSocket;

